/**
 * Everything that makes a noise and is not a car.
 *
 * `audio.ts` owns the cars — engines, skids, horns, the start signal — and every clip it
 * asks for ships with the game. This owns the other half: the weather, the city around
 * the route, and a short sound for each of the things that happen to a player during a
 * race. Almost none of *those* files exist yet, and that is the shape of this module
 * rather than a gap in it. **Every cue below is wired, mixed and documented now, and
 * stays silent until somebody drops a file next to it.** Adding one is a copy into
 * `public/sounds/env/` and a reload: nothing to rebuild, nothing to wire, no code to touch
 * and — see Files, below — nothing to declare either.
 *
 * It inherits `audio.ts`'s first rule and leans on it much harder, because here a missing
 * file is the normal case rather than a failure: no `AudioContext`, a 404, a codec the
 * browser will not decode — every one of those leaves a silent but otherwise completely
 * normal game behind, with nothing on the console.
 *
 * ## What plays when
 *
 * **Beds** loop for as long as their condition holds and are mixed by `bedMix`, which is a
 * pure function of the scene so the whole mix can be argued about in a unit test rather
 * than by listening to it.
 *
 * | bed | plays | how loud |
 * | --- | --- | --- |
 * | `rain-light` | it is raining at the region | rises with the rate, backs off as `rain-heavy` takes over |
 * | `rain-heavy` | the rain is above about a third of full | rises with the rate |
 * | `wind` | always, outdoors | a floor, plus a storm, plus snow, plus your own speed |
 * | `city-day` | the city's lights are still off | falls with speed and with heavy rain |
 * | `city-night` | the city's lights are on — crossfaded against `city-day` by the same dusk ramp the windows use | as above |
 * | `offroad` | driving with the wheels off the carriageway | rises with speed, snaps in and out |
 * | `crossing-bell` | a level crossing near the camera has its lights on | by distance to it |
 * | `train` | a train is moving near the camera | by distance, and by how fast it is going |
 *
 * **Cues** are one-shots, each with its own cooldown so a burst is one sound.
 *
 * | cue | plays when |
 * | --- | --- |
 * | `thunder-close` | a nearby lightning strike, delayed by the time the sound takes to arrive |
 * | `thunder-distant` | a far one, delayed further and quieter — see `thunderFor` |
 * | `checkpoint` | your car passes a checkpoint |
 * | `finish` | your car crosses the finish line |
 * | `victory` | the results arrive and you won |
 * | `podium` | a tour ends and the cup screen comes up |
 * | `crash-soft` | a graze — a kerb, a wall taken at an angle, a nudge from another car |
 * | `crash-hard` | a real hit, above `CRASH_HARD_SPEED` in `game.ts` |
 * | `respawn` | `R`, the touch button, or the autopilot digging itself out |
 * | `overtake` | you gain a place while racing |
 * | `overtaken` | you lose one |
 * | `chat` | somebody else says something |
 * | `join` | a player appears in the lobby |
 * | `leave` | a player disappears from it |
 * | `ready` | somebody readies up |
 * | `menu-open` / `menu-close` | the `Esc` menu |
 *
 * ## Files
 *
 * A cue named `crash-hard` is `public/sounds/env/crash-hard.ogg`, and that is the whole
 * convention. The container does not matter — anything the browser will decode — and
 * numbered variants (`crash-hard-2.ogg`, `crash-hard-3.ogg`) are picked up as alternatives
 * and chosen between at random, which is what stops a sound heard forty times a race from
 * announcing itself as one sample. Nothing has to be declared anywhere: the directory is
 * read by the server and asked for once (`/api/env-sounds`), so the client only ever
 * requests files that are actually there.
 *
 * That listing is why this can wire up twenty-odd cues against three recordings without
 * putting a line on the console. Discovering the same thing by asking for every candidate
 * would mean a 404 each, and Chrome logs a failed request as a console error whether the
 * code handling it minds or not. If the listing itself is unavailable — an older server,
 * a client served from somewhere else entirely — it falls back to asking for `<name>.ogg`
 * and `<name>.m4a`, which still works and is merely noisier.
 *
 * `env.json` is optional and exists for the cases the convention cannot express: a file
 * under a different name (`rain-light` is the committed `lightrain.ogg`), a recording that
 * wants a different level, or a cue that should come from somewhere else entirely.
 *
 * Two beds are made rather than recorded when nothing is installed for them: the crossing
 * bell and the train (see `SYNTHESISED`). A level crossing whose lights flash in silence
 * reads as broken, and an electronic bell is two struck partials and a decay — a recording
 * dropped in under the same name still wins.
 *
 * Everything is loaded once, at the first user gesture, and misses are remembered: a file
 * that was not there when the page loaded is not there until the page is reloaded. Loading
 * a cue lazily on first use instead would make it silent exactly when it mattered and
 * audible ever after, which is worse than silence throughout.
 */

/** Looping. One source each, created on first need and then left running, silent through
 *  its own gain — same reasoning as the engine loop in `audio.ts`. */
export type AmbienceBed =
  | 'rain-light'
  | 'rain-heavy'
  | 'wind'
  | 'city-day'
  | 'city-night'
  | 'offroad'
  | 'crossing-bell'
  | 'train';

/** One-shots. */
export type AmbienceCue =
  | 'thunder-close'
  | 'thunder-distant'
  | 'checkpoint'
  | 'finish'
  | 'victory'
  | 'podium'
  | 'crash-soft'
  | 'crash-hard'
  | 'respawn'
  | 'overtake'
  | 'overtaken'
  | 'chat'
  | 'join'
  | 'leave'
  | 'ready'
  | 'menu-open'
  | 'menu-close';

const BASE = '/sounds/env';
const MANIFEST_URL = `${BASE}/env.json`;
/** What the server says is installed. Its absence is survivable — see the module comment. */
const LISTING_URL = '/api/env-sounds';
/** The two containers `audio.ts` already negotiates between. Only used as a guess when
 *  there is no listing to consult, or for a manifest entry that names no container. */
const FORMATS = ['ogg', 'm4a'] as const;

/**
 * One knob for the whole environment, under `audio.ts`'s master (so `M` and the volume
 * ramp cover it for free, and so the cars are never fighting the weather for headroom).
 * The per-cue and per-bed levels below are all relative to this.
 */
const BUS_VOLUME = 0.85;

interface BedSpec {
  /** What a mix of 1 actually means, before the manifest gets a say. Beds sit under
   *  everything else: a rain loop at full scale drowns the engine it is raining on. */
  ceiling: number;
  /** How fast the gain chases its target, as an exponential rate. Weather changes over
   *  minutes and fades slowly; the surface under the wheels changes in an instant. */
  rate: number;
}

const BEDS: Record<AmbienceBed, BedSpec> = {
  'rain-light': { ceiling: 0.5, rate: 1.1 },
  'rain-heavy': { ceiling: 0.6, rate: 1.1 },
  wind: { ceiling: 0.35, rate: 0.9 },
  'city-day': { ceiling: 0.32, rate: 0.8 },
  'city-night': { ceiling: 0.3, rate: 0.8 },
  offroad: { ceiling: 0.55, rate: 9 },
  'crossing-bell': { ceiling: 0.45, rate: 6 },
  train: { ceiling: 0.6, rate: 2.5 },
};

interface CueSpec {
  gain: number;
  /** Seconds. Every one of these can be provoked several times in a frame or a broadcast —
   *  five AI drivers joining at once, a car scraping a wall for half a second — and the
   *  cooldown is what turns each of those into one sound. */
  cooldown: number;
}

const CUES: Record<AmbienceCue, CueSpec> = {
  'thunder-close': { gain: 1, cooldown: 0.4 },
  'thunder-distant': { gain: 0.8, cooldown: 0.4 },
  checkpoint: { gain: 0.7, cooldown: 0.4 },
  finish: { gain: 0.9, cooldown: 1 },
  victory: { gain: 0.9, cooldown: 1 },
  podium: { gain: 0.9, cooldown: 1 },
  'crash-soft': { gain: 0.55, cooldown: 0.25 },
  'crash-hard': { gain: 0.9, cooldown: 0.4 },
  respawn: { gain: 0.6, cooldown: 0.5 },
  overtake: { gain: 0.6, cooldown: 1.5 },
  overtaken: { gain: 0.5, cooldown: 1.5 },
  chat: { gain: 0.45, cooldown: 0.6 },
  join: { gain: 0.5, cooldown: 0.9 },
  leave: { gain: 0.5, cooldown: 0.9 },
  ready: { gain: 0.4, cooldown: 0.5 },
  'menu-open': { gain: 0.35, cooldown: 0.12 },
  'menu-close': { gain: 0.35, cooldown: 0.12 },
};

/** The committed `env.json`, as far as this cares. Anything else in it is ignored, which is
 *  what lets the file carry its own `_comment`. */
export interface EnvManifestEntry {
  /** Filenames under `public/sounds/env/`. More than one means variants, picked at random
   *  per play — the reason two thunder recordings do not read as a tape loop. */
  files?: string | string[];
  /** Overrides `CueSpec.gain` for a cue, `BedSpec.ceiling` for a bed. For a recording that
   *  is simply louder or quieter than the one these numbers were chosen against. */
  gain?: number;
  /** Cues only. Overrides `CueSpec.cooldown`. */
  cooldown?: number;
}

export type EnvManifest = Record<string, EnvManifestEntry>;

/** What the world sounds like this frame. Everything here is something the renderer or the
 *  car already knows; nothing is computed for the benefit of the audio. */
export interface AmbienceScene {
  /** Rain rate, 0..1. Zero when it is not raining, whatever else the sky is doing. */
  rain: number;
  /** Snowfall rate, 0..1. */
  snow: number;
  /** A thunderstorm. */
  storm: boolean;
  /** How far the city's lights have come on, 0..1 — `WeatherLook.night`. */
  night: number;
  /** Forward speed, m/s, signed. */
  speed: number;
  onRoad: boolean;
  /** False on the grid, during the countdown and after the finish: the city carries on,
   *  but a car that is not being driven is not throwing up gravel. */
  driving: boolean;
  /** How near the nearest ringing level crossing is, 1 beside it to 0 out of earshot. */
  bell?: number;
  /** How near and how fast the nearest moving train is, 0..1. */
  train?: number;
}

/** Read by the Playwright suite through `AudioDebug.env` — see `getAudio` in `main.ts`. */
export interface AmbienceDebug {
  /** An `AudioContext` exists and the bus is built. */
  attached: boolean;
  /** Cues and beds with at least one decoded recording behind them. */
  loaded: string[];
  /** Cues and beds with no recording behind them. The expected state for most of them, and
   *  the first list to read when a sound you dropped in is not playing. */
  missing: string[];
  /** Every file the server says is installed, or null if it could not be asked — the second
   *  list to read, since it answers "is my file even being seen" separately from "will this
   *  browser decode it". */
  installed: string[] | null;
  /** Live gain of every bed that has ever been audible, 0..1. */
  beds: Record<string, number>;
  /** How many times each cue has actually sounded. Absent means never. */
  cues: Record<string, number>;
  /** Thunder claps still in the air. */
  pending: number;
}

interface Bed {
  gain: GainNode;
  source: AudioBufferSourceNode;
}

interface PendingCue {
  cue: AmbienceCue;
  gain: number;
  /** Seconds left before it sounds. */
  delay: number;
}

/**
 * How much thunder is allowed to be in the air at once.
 *
 * A storm flashes every 3.5–15 s and a clap can be 5.5 s behind its flash, so two in the
 * air is ordinary and three is possible. This is not tuning, it is a bound: `thunder()` is
 * called by the renderer and drained by the game's frame loop, and nothing in the type
 * system says those two run at the same rate.
 */
const MAX_PENDING = 4;

class Ambience {
  private context: AudioContext | null = null;
  private bus: GainNode | null = null;

  /** One entry per cue or bed that loaded, holding its variants. */
  private readonly buffers = new Map<string, AudioBuffer[]>();
  /** Probed and not there. Never retried — see the module comment. */
  private readonly absent = new Set<string>();
  private readonly overrides = new Map<string, EnvManifestEntry>();
  /** The installed files, as the server listed them; null if it could not be asked. */
  private listing: string[] | null = null;
  private readonly beds = new Map<AmbienceBed, Bed>();
  private readonly lastPlayed = new Map<AmbienceCue, number>();
  private readonly counts: Record<string, number> = {};
  private pending: PendingCue[] = [];
  private loading = false;

  /**
   * Handed the graph `audio.ts` has already built, on the first user gesture. Idempotent:
   * `unlock` is called on every click until the context is actually running.
   */
  attach(context: AudioContext, master: GainNode): void {
    if (this.context === context) return;
    try {
      this.context = context;
      this.bus = context.createGain();
      this.bus.gain.value = BUS_VOLUME;
      this.bus.connect(master);
      void this.load();
    } catch {
      this.context = null;
      this.bus = null;
    }
  }

  /**
   * A one-shot, if there is a file for it and it is not still in cooldown.
   *
   * `scale` is for a caller that knows something about *this* instance of the cue — how
   * far away the lightning was, how hard the hit landed — and is multiplied into the clip's
   * own level rather than replacing it.
   */
  play(cue: AmbienceCue, scale = 1): boolean {
    const context = this.context;
    const bus = this.bus;
    const variants = this.buffers.get(cue);
    if (!context || !bus || !variants?.length) return false;

    const override = this.overrides.get(cue);
    const cooldown = override?.cooldown ?? CUES[cue].cooldown;
    if (context.currentTime - (this.lastPlayed.get(cue) ?? -Infinity) < cooldown) return false;
    this.lastPlayed.set(cue, context.currentTime);

    try {
      const source = context.createBufferSource();
      source.buffer = variants[Math.floor(Math.random() * variants.length)] ?? variants[0];
      const node = context.createGain();
      node.gain.value = clamp01((override?.gain ?? CUES[cue].gain) * scale);
      source.connect(node);
      node.connect(bus);
      source.start();
      source.onended = () => {
        source.disconnect();
        node.disconnect();
      };
      this.counts[cue] = (this.counts[cue] ?? 0) + 1;
      return true;
    } catch {
      // A cue that will not play is not worth a word to anyone.
      return false;
    }
  }

  /**
   * A lightning strike happened. The clap is queued rather than played, because the gap
   * between the flash and the sound is the only thing that says how far away it was — and
   * it is the difference between weather and a sound effect.
   */
  thunder(): void {
    if (this.pending.length >= MAX_PENDING) return;
    const { cue, delay, gain } = thunderFor(Math.random());
    this.pending.push({ cue, gain, delay });
  }

  /**
   * Per frame, from the game. Advances the queued thunder and walks every bed's gain
   * towards what `bedMix` says this frame's scene should sound like.
   *
   * Beds are created on first need rather than at load: a bed that never comes up — the
   * off-road loop in a clean race, rain in Kraków in July — should cost nothing at all.
   */
  update(scene: AmbienceScene, dt: number): void {
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const clap = this.pending[i];
      clap.delay -= dt;
      if (clap.delay > 0) continue;
      this.pending.splice(i, 1);
      this.play(clap.cue, clap.gain);
    }

    if (!this.context || !this.bus) return;
    const mix = bedMix(scene);
    for (const name of Object.keys(BEDS) as AmbienceBed[]) {
      const spec = BEDS[name];
      const target = clamp01(mix[name]) * (this.overrides.get(name)?.gain ?? spec.ceiling);
      const bed = this.ensureBed(name, target);
      if (!bed) continue;
      const k = Math.min(1, dt * spec.rate);
      bed.gain.gain.value += (target - bed.gain.gain.value) * k;
    }
  }

  /**
   * The game screen went away. Cuts every bed and drops thunder that has not landed —
   * nothing is going to call `update` again, so a bed left at its current gain would hum
   * away under the lobby exactly as the engine used to.
   */
  silence(): void {
    this.pending = [];
    for (const bed of this.beds.values()) bed.gain.gain.value = 0;
  }

  debug(): AmbienceDebug {
    const beds: Record<string, number> = {};
    for (const [name, bed] of this.beds) beds[name] = bed.gain.gain.value;
    return {
      attached: this.bus !== null,
      loaded: [...this.buffers.keys()],
      missing: [...this.absent],
      installed: this.listing,
      beds,
      cues: { ...this.counts },
      pending: this.pending.length,
    };
  }

  // -------------------------------------------------------------------------
  // internals
  // -------------------------------------------------------------------------

  private ensureBed(name: AmbienceBed, target: number): Bed | null {
    const existing = this.beds.get(name);
    if (existing) return existing;
    // Not "is there a file" but "is this bed wanted yet": a silent looping source is cheap
    // but not free, and most races want two of the six.
    if (target <= 0.002) return null;
    const context = this.context;
    const buffer = this.buffers.get(name)?.[0];
    if (!context || !this.bus || !buffer) return null;
    try {
      const gain = context.createGain();
      gain.gain.value = 0;
      gain.connect(this.bus);
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      // Deliberately no loop-point refinement, unlike the engine: these are long recordings
      // of noise, where a seam is inaudible, rather than 2.6 s of periodic engine where it
      // is a click every time round.
      source.connect(gain);
      // A random start so two players in the same storm are not hearing the same rain, and
      // so a bed that fades out and back in does not restart from the same crack of thunder.
      source.start(0, Math.random() * buffer.duration);
      const bed: Bed = { gain, source };
      this.beds.set(name, bed);
      return bed;
    } catch {
      return null;
    }
  }

  /**
   * One pass over every cue and bed, in series.
   *
   * In series rather than at once because this runs behind a game that is already fetching
   * a world: a dozen parallel requests on the first click would be competing with the
   * download the player is actually waiting for, and nothing here is urgent — the first
   * checkpoint of the first race is most of a minute away.
   */
  private async load(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    try {
      const [manifest, listing] = await Promise.all([this.loadManifest(), this.loadListing()]);
      for (const [name, entry] of Object.entries(manifest)) this.overrides.set(name, entry);
      this.listing = listing;

      const names = [...Object.keys(BEDS), ...Object.keys(CUES)];
      for (const name of names) {
        if (this.buffers.has(name) || this.absent.has(name)) continue;
        const decoded: AudioBuffer[] = [];
        for (const file of filesFor(name, manifest[name], listing)) {
          const buffer = await this.decode(file);
          if (buffer) decoded.push(buffer);
        }
        const made = decoded.length ? null : synthesise(name, this.context);
        if (decoded.length) this.buffers.set(name, decoded);
        else if (made) this.buffers.set(name, [made]);
        else this.absent.add(name);
      }
    } finally {
      this.loading = false;
    }
  }

  private async loadManifest(): Promise<EnvManifest> {
    try {
      const response = await fetch(MANIFEST_URL, { cache: 'no-cache' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return parseManifest(await response.json());
    } catch {
      // No manifest is not a broken install — it is the *normal* one. Everything the
      // convention can express needs no manifest at all.
      return {};
    }
  }

  /** What the server says is in `public/sounds/env`. Null — not an empty list — when there
   *  was no answer, since "nothing installed" and "nobody to ask" lead to opposite
   *  behaviour: one means load nothing, the other means guess. */
  private async loadListing(): Promise<string[] | null> {
    try {
      const response = await fetch(LISTING_URL, { cache: 'no-cache' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = (await response.json()) as { files?: unknown };
      if (!Array.isArray(body.files)) return null;
      return body.files.filter((f): f is string => typeof f === 'string');
    } catch {
      return null;
    }
  }

  /** The first container of `file` this browser will decode, or null if there is none. */
  private async decode(file: string): Promise<AudioBuffer | null> {
    const context = this.context;
    if (!context) return null;
    for (const url of candidateUrls(file)) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        return await context.decodeAudioData(await response.arrayBuffer());
      } catch {
        // Try the other container; if none work this clip is simply never heard.
      }
    }
    return null;
  }
}

// ---------------------------------------------------------------------------
// the mix
// ---------------------------------------------------------------------------

/** Speed at which the speed-dependent beds are at full effect, m/s — about 145 km/h. */
const MIX_TOP_SPEED = 40;
/** Speed at which the off-road bed is at full, m/s. Sliding onto a pavement at walking
 *  pace is not a sound; leaving the road at 40 km/h is. */
const OFFROAD_FULL_SPEED = 12;

/**
 * The whole environment mix, as numbers, from the scene.
 *
 * Pure, and exported, because every judgement worth arguing about is in here and a mix you
 * can only inspect by listening to it is a mix nobody will ever check — the same reasoning
 * that keeps `lookFor` separate from the renderer in `render/weather.ts`.
 *
 * Each value is 0..1 against that bed's own ceiling, not an absolute level.
 */
export function bedMix(scene: AmbienceScene): Record<AmbienceBed, number> {
  const rain = clamp01(scene.rain);
  const snow = clamp01(scene.snow);
  const speed = clamp01(Math.abs(scene.speed) / MIX_TOP_SPEED);

  // Two rain loops rather than one turned up: rain gets *denser* as it gets heavier, not
  // merely louder, and a light-rain recording at full gain sounds like a light recording
  // played loudly. They cross over rather than switch, so a shower building up is a change
  // in texture instead of a cut.
  const heavy = clamp01((rain - 0.35) / 0.45);

  // A city is quieter when it is pouring and, from inside a car at speed, quieter still:
  // by the time you are doing 145 km/h you are hearing your own car and nothing else.
  const city = clamp01((1 - 0.65 * speed) * (1 - 0.45 * rain));

  return {
    'rain-light': rain > 0 ? clamp01(0.4 + rain * 1.2) * (1 - 0.55 * heavy) : 0,
    'rain-heavy': heavy,
    // A floor, because outdoors is never truly still, and then everything that moves air:
    // a storm, falling snow, and the car's own passage through it.
    wind: clamp01(0.1 + (scene.storm ? 0.3 : 0) + 0.25 * snow + 0.5 * speed),
    'city-day': city * (1 - clamp01(scene.night)),
    'city-night': city * clamp01(scene.night),
    offroad:
      scene.driving && !scene.onRoad ? clamp01(Math.abs(scene.speed) / OFFROAD_FULL_SPEED) : 0,
    // Squared, so the bell is a sound of the crossing you are at rather than of the town.
    'crossing-bell': clamp01(scene.bell ?? 0) ** 2,
    train: clamp01(scene.train ?? 0),
  };
}

// ---------------------------------------------------------------------------
// made, not recorded
// ---------------------------------------------------------------------------

/** Beds with a sound of their own when nobody has installed one. */
const SYNTHESISED: Partial<Record<string, (context: BaseAudioContext) => AudioBuffer>> = {
  'crossing-bell': crossingBell,
  train: trainRumble,
};

function synthesise(name: string, context: BaseAudioContext | null): AudioBuffer | null {
  const make = SYNTHESISED[name];
  if (!make || !context) return null;
  try {
    return make(context);
  } catch {
    return null;
  }
}

/**
 * The electronic bell of a Polish level crossing: a bright two-partial strike a little under
 * twice a second, each dying away before the next. One second of loop, two strikes.
 */
export function crossingBell(context: BaseAudioContext): AudioBuffer {
  const rate = context.sampleRate;
  const buffer = context.createBuffer(1, Math.round(rate), rate);
  const data = buffer.getChannelData(0);
  const strikes = [0, 0.5];
  const partials: [number, number][] = [
    [1180, 0.55],
    [2950, 0.25],
    [4410, 0.12],
  ];
  for (let i = 0; i < data.length; i++) {
    const t = i / rate;
    let v = 0;
    for (const at of strikes) {
      const d = t - at;
      if (d < 0) continue;
      const env = Math.exp(-d * 9) * Math.min(1, d * 800);
      for (const [f, a] of partials) v += Math.sin(2 * Math.PI * f * d) * a * env;
    }
    data[i] = v * 0.6;
  }
  return buffer;
}

/**
 * A train going by: low rumble from filtered noise, and the double clack of a bogie over a
 * rail joint four times a second. Deterministic noise, so the loop is the same every time
 * and its seam is where the clacks are.
 */
export function trainRumble(context: BaseAudioContext): AudioBuffer {
  const rate = context.sampleRate;
  const seconds = 2;
  const buffer = context.createBuffer(1, Math.round(rate * seconds), rate);
  const data = buffer.getChannelData(0);
  let seed = 12345;
  let low = 0;
  let lower = 0;
  for (let i = 0; i < data.length; i++) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const white = seed / 4294967296 - 0.5;
    low += (white - low) * 0.05;
    lower += (low - lower) * 0.02;
    const t = i / rate;
    let clack = 0;
    for (const at of [0, 0.11, 0.5, 0.61, 1, 1.11, 1.5, 1.61]) {
      const d = t - at;
      if (d >= 0 && d < 0.06) clack += Math.exp(-d * 90) * Math.sin(2 * Math.PI * 180 * d);
    }
    data[i] = lower * 3.5 + low * 0.6 + clack * 0.35;
  }
  return buffer;
}

/** Metres per second. */
const SPEED_OF_SOUND = 343;
/** Farthest strike that still gets a sound, in seconds of travel — about 1.9 km. */
const THUNDER_MAX_DELAY = 5.5;
/** Nearest, in seconds — about 100 m, which is as close as a strike gets before the flash
 *  and the crack are the same event and the delay stops meaning anything. */
const THUNDER_MIN_DELAY = 0.3;
/** Under this, the strike is close enough for the sharp recording rather than the rumble. */
const THUNDER_CLOSE_DELAY = 2;

/**
 * Which thunder a flash earns, and how long after it.
 *
 * The flash is instant and the sound is not, so the gap is the distance: at
 * `SPEED_OF_SOUND` the range below is roughly 100 m to 1.9 km. Squaring the roll biases the
 * storm towards the far end, which is both what a storm mostly is and the case that sounds
 * least like a sound effect — a crack on top of every flash reads as a strobe with a
 * sample attached.
 *
 * Takes the roll rather than calling `Math.random` so the mapping can be tested at both
 * ends without stubbing anything.
 */
export function thunderFor(roll: number): {
  cue: 'thunder-close' | 'thunder-distant';
  /** Seconds. */
  delay: number;
  /** Multiplied into the cue's own level — distance is most of what makes thunder quiet. */
  gain: number;
  /** Metres, for the debug readout and for the tests. */
  distance: number;
} {
  const unit = clamp01(roll);
  const delay = THUNDER_MIN_DELAY + unit * unit * (THUNDER_MAX_DELAY - THUNDER_MIN_DELAY);
  const close = delay < THUNDER_CLOSE_DELAY;
  return {
    cue: close ? 'thunder-close' : 'thunder-distant',
    delay,
    gain: clamp(1.05 - 0.11 * delay, 0.35, 1),
    distance: delay * SPEED_OF_SOUND,
  };
}

// ---------------------------------------------------------------------------
// files
// ---------------------------------------------------------------------------

/**
 * The manifest, defended against being hand-edited into anything at all — it is a file
 * somebody drops sounds next to, so a stray string or a missing brace has to degrade to
 * "that entry does not exist" rather than to a broken audio layer.
 */
export function parseManifest(body: unknown): EnvManifest {
  if (!body || typeof body !== 'object') return {};
  const out: EnvManifest = {};
  for (const [name, raw] of Object.entries(body as Record<string, unknown>)) {
    // `_comment` and anything else that is not an object; the manifest documents itself.
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const entry = raw as EnvManifestEntry;
    const files = normaliseFiles(entry.files);
    const parsed: EnvManifestEntry = {};
    if (files.length) parsed.files = files;
    if (Number.isFinite(entry.gain)) parsed.gain = clamp01(Number(entry.gain));
    if (Number.isFinite(entry.cooldown)) parsed.cooldown = Math.max(0, Number(entry.cooldown));
    out[name] = parsed;
  }
  return out;
}

function normaliseFiles(files: EnvManifestEntry['files']): string[] {
  const list = typeof files === 'string' ? [files] : Array.isArray(files) ? files : [];
  return list.filter((f): f is string => typeof f === 'string' && f.trim() !== '').map((f) => f.trim());
}

/**
 * Every recording a cue or bed should play, given what the manifest says and what the
 * server says is on disk.
 *
 * The manifest wins where it speaks: a cue that names its files is asking for those files,
 * and they are still checked against the listing so that a typo is a silent cue rather than
 * a request that fails — except for an absolute path or a URL, which is by definition not
 * in this directory and is taken on trust.
 *
 * Otherwise the directory *is* the configuration. `crash-hard.ogg` is the cue; so are
 * `crash-hard-2.ogg`, `crash-hard_3.wav` and `crash-hard.4.m4a`, which is the whole story
 * of how to add a variant. The digits are what keeps that from being a prefix match: no cue
 * name is another cue name followed by a number, so `rain-light` can never swallow
 * `rain-heavy`.
 *
 * With no listing at all there is nothing to discover from, so it falls back to the name
 * itself and lets `candidateUrls` guess at a container.
 */
export function filesFor(
  name: string,
  entry: EnvManifestEntry | undefined,
  listing: string[] | null,
): string[] {
  const named = normaliseFiles(entry?.files);
  if (named.length) {
    if (!listing) return named;
    return named.filter((file) => isRemote(file) || listing.includes(file));
  }
  if (!listing) return [name];
  const variant = new RegExp(`^${escapeForRegExp(name)}([-_.]?\\d+)?\\.[a-z0-9]+$`, 'i');
  return listing.filter((file) => variant.test(file));
}

function escapeForRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isRemote(file: string): boolean {
  return /^(https?:)?\/\//.test(file.trim()) || file.trim().startsWith('/');
}

/**
 * Where a manifest filename actually lives. A name with an extension is taken at its word;
 * one without is probed across the containers the game already ships in. A leading slash or
 * a protocol is left alone, so a bed can come from somewhere else entirely.
 */
export function candidateUrls(file: string): string[] {
  const trimmed = file.trim();
  const base = isRemote(trimmed) ? trimmed : `${BASE}/${trimmed}`;
  // Only a real extension counts — a name like `thunder-1.2` is a filename, not a container.
  if (/\.[a-z0-9]{2,4}$/i.test(trimmed)) return [base];
  return FORMATS.map((format) => `${base}.${format}`);
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/** One instance for the page, for the same reason as `carAudio` and `musicPlayer`: the
 *  `Game` is rebuilt whenever the host picks a different region, and the weather outside is
 *  not something that should be re-decoded when it does. */
export const ambience = new Ambience();
